import { isDealActive } from '@/lib/utils.ts';
import { db } from '@/models/db';
import { hydrateFromWorker } from '@/models/hydrate-worker-utils.ts';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { TaskTemplate, TaskV2 } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import { dealStore } from '@/models/stores/deals';
import {
  AssigneeType,
  TaskableType,
  TaskStatus,
  type TaskTemplate as TaskTemplateType,
  type TaskV2 as TaskTypeV2,
} from '@/models/types.ts';
import {
  type IObservableArray,
  makeAutoObservable,
  observable,
  type ObservableMap,
} from 'mobx';

class TaskTemplateStore implements BaseStore<TaskTemplate, TaskTemplateType> {
  store: ObservableMap<string, TaskTemplate> = observable.map();

  // stores categorized by taskable type, for display purposes (O(1) getter vs. O(n) getter at registry level)
  private storeByTaskableType: ObservableMap<TaskableType, TaskTemplate[]> =
    observable.map();

  constructor() {
    makeAutoObservable(this);
    this.initializeTaskableArrs();
  }

  private initializeTaskableArrs() {
    for (const taskableType of Object.values(TaskableType)) {
      this.storeByTaskableType.set(taskableType, []);
    }
  }

  private addToTaskable(taskTemplate: TaskTemplate) {
    const taskableArray = this.storeByTaskableType.get(
      taskTemplate.taskable_type,
    );
    if (taskableArray) {
      taskableArray.push(taskTemplate);
    }
  }

  private removeFromTaskable(taskTemplate: TaskTemplate) {
    const taskableArray = this.storeByTaskableType.get(
      taskTemplate.taskable_type,
    );
    if (taskableArray) {
      const index = taskableArray.findIndex((t) => t.id === taskTemplate.id);
      if (index !== -1) {
        taskableArray.splice(index, 1);
      }
    }
  }

  private setTaskTemplateField = <K extends keyof TaskTemplateType>(
    taskTemplate: TaskTemplate,
    key: K,
    value: TaskTemplateType[K],
  ) => {
    (taskTemplate as Pick<TaskTemplate, K>)[key] = value as TaskTemplate[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TaskTemplateType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.task_templates.delete(delta.record_id);
      }
      this.removeTaskTemplate(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.task_templates.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.task_templates.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.task_templates.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<TaskTemplateType>;
  }) {
    const existingTemplate = this.getById(delta.record_id);
    if (!existingTemplate) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof TaskTemplateType,
      TaskTemplateType[keyof TaskTemplateType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingTemplate[key] === newValue) {
        continue;
      }

      this.setTaskTemplateField(existingTemplate, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingTemplate);
  }

  getTaskTemplatesByTaskableType(taskableType: TaskableType): TaskTemplate[] {
    return this.storeByTaskableType.get(taskableType) || [];
  }

  getById(id: string): TaskTemplate | undefined {
    return this.store.get(id);
  }

  add(input: TaskTemplateType) {
    const taskTemplate = new TaskTemplate(input);
    this.store.set(taskTemplate.id, taskTemplate);
    this.addToTaskable(taskTemplate);
  }

  removeTaskTemplate(id: TaskTemplateType['id']) {
    this.store.delete(id);
  }

  get all(): TaskTemplate[] {
    return Array.from(this.store.values());
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<TaskTemplateType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TaskTemplateType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.task_templates.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.removeTaskTemplate(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.removeTaskTemplate(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  upsert(taskTemplate: TaskTemplateType) {
    const existingTaskTemplate = this.getById(taskTemplate.id);
    if (existingTaskTemplate) {
      this.replace({ id: taskTemplate.id, taskTemplate });
    } else {
      this.add(taskTemplate);
    }
  }

  replace({
    id,
    taskTemplate,
  }: {
    id: string;
    taskTemplate: TaskTemplateType;
  }) {
    const existingTaskTemplate = this.getById(id);
    if (!existingTaskTemplate) return;

    const taskableChanged =
      existingTaskTemplate.taskable_type !== taskTemplate.taskable_type;
    if (taskableChanged) {
      this.removeFromTaskable(existingTaskTemplate);
    }

    existingTaskTemplate.description = taskTemplate.description;
    existingTaskTemplate.title = taskTemplate.title;
    existingTaskTemplate.taskable_type = taskTemplate.taskable_type;

    if (taskableChanged) {
      this.addToTaskable(existingTaskTemplate);
    }

    this.save(existingTaskTemplate);
  }

  save(taskTemplate: TaskTemplate) {
    this.store.set(taskTemplate.id, taskTemplate);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'task_templates',
      entity: 'task_templates',
      modelStore: taskTemplateStore,
    });
  }
}

export const taskTemplateStore = new TaskTemplateStore();

// to replace AssignedTasksStore, not TasksStore
class TasksStoreV2 implements BaseStore<TaskV2, TaskTypeV2> {
  store: ObservableMap<string, TaskV2> = observable.map();
  private _empty: IObservableArray<TaskV2> = observable.array([], {
    deep: false,
  });

  // indexes for different taskable types
  storeIndexedByDealId: ObservableMap<string, TaskV2[]> = observable.map();
  storeIndexedByAppraisalId: ObservableMap<string, TaskV2[]> = observable.map();

  private taskableTypeToIndexMap = {
    [TaskableType.Deal]: this.storeIndexedByDealId,
    [TaskableType.Appraisal]: this.storeIndexedByAppraisalId,
  };

  constructor() {
    makeAutoObservable(this);
  }

  private getOrCreateCollection(
    map: ObservableMap<string, TaskV2[]>,
    key: string,
  ): TaskV2[] {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<TaskV2>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  private setTaskField = <K extends keyof TaskTypeV2>(
    task: TaskV2,
    key: K,
    value: TaskTypeV2[K],
  ) => {
    (task as Pick<TaskV2, K>)[key] = value as TaskV2[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TaskTypeV2>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.tasks.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.tasks.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.tasks.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.tasks.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<TaskTypeV2>;
  }) {
    const existingTask = this.getById(delta.record_id);
    if (!existingTask) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof TaskTypeV2,
      TaskTypeV2[keyof TaskTypeV2],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingTask[key] === newValue) {
        continue;
      }

      this.setTaskField(existingTask, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingTask);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<TaskTypeV2>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<TaskTypeV2>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.tasks.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): TaskV2 | undefined {
    return this.store.get(id);
  }

  getByTarget(targetId: string, targetType: TaskableType): TaskV2[] {
    const indexMap = this.taskableTypeToIndexMap[targetType];
    if (!indexMap) return this._empty;
    return this.getOrCreateCollection(indexMap, targetId);
  }

  addToIndexes(task: TaskV2) {
    const { taskable_id: taskableId, taskable_type: taskableType } = task;
    const indexMap = this.taskableTypeToIndexMap[taskableType];

    this.getOrCreateCollection(indexMap, taskableId).push(task);
  }

  removeFromIndexes(task: TaskV2) {
    const { taskable_id: taskableId, taskable_type: taskableType } = task;
    const indexMap = this.taskableTypeToIndexMap[taskableType];

    const removeFrom = (arr: TaskV2[]) => {
      const i = arr.findIndex((t) => t.id === task.id);
      if (i >= 0) arr.splice(i, 1);
    };

    removeFrom(this.getOrCreateCollection(indexMap, taskableId));
  }

  get all() {
    return Array.from(this.store.values());
  }

  get allForActiveDeals(): TaskV2[] {
    return Array.from(this.taskableTypeToIndexMap[TaskableType.Deal].values())
      .flat()
      .filter((task) => {
        const deal = dealStore.getById(task.taskable_id);
        return isDealActive(deal);
      })
      .sort((a, b) => {
        if (a.status === TaskStatus.Incomplete && b.status === TaskStatus.Done)
          return -1;
        if (a.status === TaskStatus.Done && b.status === TaskStatus.Incomplete)
          return 1;

        const aIsUnassigned = !a.assignee_id;
        const bIsUnassigned = !b.assignee_id;

        if (aIsUnassigned && !bIsUnassigned) return -1;
        if (!aIsUnassigned && bIsUnassigned) return 1;

        return 0;
      });
  }

  // same as allForActiveDeals, but to support all entities
  get allTasksForActiveEntities() {
    return this.all
      .filter((task) => {
        switch (task.taskable_type) {
          case TaskableType.Deal: {
            const deal = dealStore.getById(task.taskable_id);
            return isDealActive(deal);
          }
          case TaskableType.Appraisal: {
            // TODO -- check if appraisal is active, whenever that is implemented/possible
            return true;
          }
          default:
            // exclude unknown types
            return false;
        }
      })
      .sort((a, b) => {
        if (a.status === TaskStatus.Incomplete && b.status === TaskStatus.Done)
          return -1;
        if (a.status === TaskStatus.Done && b.status === TaskStatus.Incomplete)
          return 1;

        const aIsUnassigned = !a.assignee_id;
        const bIsUnassigned = !b.assignee_id;

        if (aIsUnassigned && !bIsUnassigned) return -1;
        if (!aIsUnassigned && bIsUnassigned) return 1;

        return 0;
      });
  }

  updateStatus({ id, status }: { id: string; status: TaskTypeV2['status'] }) {
    const task = this.getById(id);
    if (!task) return;

    task.status = status;
  }

  updateAssignee({
    id,
    asigneeId,
    assigneeType = AssigneeType.User,
  }: {
    id: string;
    asigneeId: TaskTypeV2['assignee_id'];
    assigneeType?: AssigneeType;
  }) {
    const task = this.getById(id);
    if (!task) return;

    task.assignee_id = asigneeId;
    task.assignee_type = assigneeType;
  }

  add(input: TaskTypeV2) {
    const task = new TaskV2(input);

    this.store.set(task.id, task);
    this.addToIndexes(task);
  }

  save(task: TaskV2) {
    this.store.set(task.id, task);

    const arr = this.getByTarget(task.taskable_id, task.taskable_type);
    const idx = arr.findIndex((t) => t.id === task.id);
    if (idx >= 0) arr.splice(idx, 1, task);
  }

  upsert(task: TaskTypeV2) {
    const existingTask = this.getById(task.id);
    if (existingTask) {
      existingTask.status = task.status;
      existingTask.title = task.title;
      existingTask.description = task.description;
      existingTask.assignee_id = task.assignee_id;
      existingTask.product_user_id = task.product_user_id;
      this.save(existingTask);
    } else {
      this.add(task);
    }
  }

  remove(id: TaskTypeV2['id']) {
    const task = this.getById(id);
    if (!task) return;
    this.store.delete(id);
    this.removeFromIndexes(task);
    db.tasks.delete(id);
  }

  replace({ id, task }: { id: string; task: TaskTypeV2 }) {
    const existingTask = this.getById(id);
    if (!existingTask) return;
    existingTask.status = task.status;
    existingTask.title = task.title;
    existingTask.description = task.description;
    existingTask.assignee_id = task.assignee_id;
    existingTask.product_user_id = task.product_user_id;
    this.save(existingTask);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'tasks',
      entity: 'tasks',
      modelStore: taskStoreV2,
    });
  }

  async hydrateDeal(dealId: TaskTypeV2['taskable_id']): Promise<void> {
    const items = await db.tasks.toArray();
    items.forEach((item) => {
      if (
        item.taskable_id === dealId &&
        item.taskable_type === TaskableType.Deal
      )
        this.upsert(item);
    });
  }
}

export const taskStoreV2 = new TasksStoreV2();
